import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Volume2, Trash2, BookMarked } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, speak } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { WordIcon } from "@/components/WordIcon";
import { LexiconForm } from "@/components/LexiconForm";
import { useTranslations, Translation } from "@/lib/translations";

export default function LexiconPage() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/teams/lexicon").then((r) => setItems(r.data)).catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  const shown = useMemo(() => (items || []).filter((w) => `${w.word} ${w.definition}`.toLowerCase().includes(q.toLowerCase())), [items, q]);
  const tr = useTranslations(shown.slice(0, 30));

  const remove = async (w) => {
    try {
      await api.delete(`/teams/lexicon/${w.id}`);
      setItems((list) => list.filter((x) => x.id !== w.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="lexicon-page">
      <Link to="/equipe" data-testid="lexicon-back-btn" className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-5 h-5" /> Équipe
      </Link>
      <div className="flex items-end justify-between">
        <h1 className="font-display text-4xl font-extrabold uppercase">Lexique de l'équipe</h1>
        <span className="font-display text-2xl font-bold text-orange-600" data-testid="lexicon-count">{items?.length ?? 0}</span>
      </div>
      <LexiconForm onAdded={load} />
      {!items ? (
        <Skeleton className="h-24 w-full" />
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center text-slate-600" data-testid="lexicon-empty">
          <BookMarked className="w-10 h-10 mx-auto mb-2 text-slate-400" /> Ajoutez les mots propres à votre chantier.
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher" className="h-12 pl-10 text-base bg-white" data-testid="lexicon-search-input" />
          </div>
          <div className="space-y-2" data-testid="lexicon-list">
            {shown.map((w, i) => (
              <div key={w.id} className="bg-white rounded-xl border-2 border-slate-200 p-4 flex items-start gap-3" data-testid={`lexicon-item-${i}`}>
                <WordIcon word={w} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-lg">{w.word}</div>
                  <Translation text={tr.map[w.word]} rtl={tr.rtl} testId={`lexicon-item-translation-${i}`} />
                  <div className="text-sm text-slate-600">{w.definition}</div>
                  {w.example && <div className="text-xs italic text-slate-500 mt-1">« {w.example} »</div>}
                  <div className="text-[11px] font-bold uppercase text-slate-400 mt-1">Ajouté par {w.author_name}</div>
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => speak(`${w.word}. ${w.example || ""}`, 0.9)} aria-label="Écouter" data-testid={`lexicon-item-audio-${i}`} className="w-10 h-10 rounded-full bg-slate-900 text-amber-400 hover:bg-orange-500 hover:text-white grid place-items-center transition-colors">
                    <Volume2 className="w-5 h-5" />
                  </button>
                  {w.can_delete && (
                    <button onClick={() => remove(w)} aria-label="Supprimer" data-testid={`lexicon-item-delete-${i}`} className="w-10 h-10 rounded-full border-2 border-slate-200 text-slate-500 hover:border-red-400 hover:text-red-600 grid place-items-center transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
